import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CommentThread, SharePortal, VersionDetail } from '@katsquare/types';
import { Button, Card, Input, SheetBackdrop, Spinner, useSettledAfterMount } from '../components/ui';
import { ApiRequestError, shareApi } from '../lib/api';
import { type DrawTool, type PendingAnchor, ProofViewer } from '../features/proofs/ProofViewer';
import {
  IconBox,
  IconChevronDown,
  IconComment,
  IconDownload,
  IconDraw,
  IconHand,
  IconPin,
  IconText,
} from '../components/icons';

/** Phone-sized layout, where the corrections rail becomes a bottom sheet. */
const isPhone = () => window.matchMedia('(max-width: 767px)').matches;

/**
 * The public review page a client opens from an external link — no account, no
 * sign-in. It deliberately renders outside AppShell: there is no workspace
 * sidebar, no navigation to anything else, and nothing here reveals the rest of
 * the account. The only thing this page can reach is the one PDF it was given.
 *
 * It is also the only place a stranger meets the product, so it carries the
 * KatSquare Draft mark and a way through to the marketing site. Those links
 * open in a new tab: a reviewer part-way through marking up a proof must never
 * lose their place to a curiosity click.
 */
export function SharePage() {
  const { token = '' } = useParams();
  const api = useMemo(() => shareApi(token), [token]);
  const queryClient = useQueryClient();

  const portal = useQuery({
    queryKey: ['share', token],
    queryFn: api.portal,
    retry: false,
  });

  const [versionId, setVersionId] = useState<string | null>(null);
  const [tool, setTool] = useState<DrawTool | null>('text');
  const [pageIndex, setPageIndex] = useState(0);
  const [pending, setPending] = useState<PendingAnchor | null>(null);
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [askName, setAskName] = useState(false);
  // Phones start with the proof at full width and the rail collapsed to a strip;
  // it comes up as a half-height sheet on demand. Desktop keeps it pinned open.
  const [commentsOpen, setCommentsOpen] = useState(() => typeof window === 'undefined' || !isPhone());

  // Default to the newest round once the portal loads.
  useEffect(() => {
    if (!versionId && portal.data?.versions.length) {
      setVersionId(portal.data.versions[0]!.id);
    }
  }, [portal.data, versionId]);

  // Ask for a name on first arrival. Skipping is fine — they post as "Guest".
  useEffect(() => {
    if (portal.data && !portal.data.guest && portal.data.canComment) {
      setAskName(sessionStorage.getItem(`ksq-share-greeted-${token}`) !== '1');
    }
  }, [portal.data, token]);

  const version = useQuery({
    queryKey: ['share-version', token, versionId],
    queryFn: () => api.version(versionId as string),
    enabled: !!versionId,
  });
  const threads = useQuery({
    queryKey: ['share-threads', token, versionId],
    queryFn: () => api.threads(versionId as string),
    enabled: !!versionId,
  });

  // Live updates, so the team's replies land without a reload.
  useEffect(() => {
    if (!versionId) return;
    const es = new EventSource(api.eventsUrl(versionId), { withCredentials: true });
    es.addEventListener('review', () => {
      queryClient.invalidateQueries({ queryKey: ['share-threads', token, versionId] });
    });
    return () => es.close();
  }, [api, token, versionId, queryClient]);

  if (portal.isPending) {
    return (
      <div className="flex h-dvh items-center justify-center bg-neutral-100">
        <Spinner />
      </div>
    );
  }

  if (portal.isError) {
    const status = portal.error instanceof ApiRequestError ? portal.error.status : 0;
    return (
      <div className="flex h-dvh flex-col bg-neutral-100">
        <BrandBar />
        <div className="flex flex-1 items-center justify-center p-6">
          <Card className="max-w-md p-8 text-center">
            <p className="mb-2 text-lg font-semibold text-neutral-900">
              {status === 404 ? 'This review link is no longer available' : 'Something went wrong'}
            </p>
            <p className="mb-6 text-sm text-neutral-500">
              {status === 404
                ? 'It may have been turned off, expired, or replaced. Ask whoever sent it for a fresh link.'
                : (portal.error as Error).message}
            </p>
            {/* A dead link is still someone's first meeting with the product. */}
            <a
              href="/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block rounded-md bg-neutral-900 px-4 py-2 text-sm font-medium text-white hover:bg-neutral-700"
            >
              See what KatSquare Draft does
            </a>
          </Card>
        </div>
      </div>
    );
  }

  const p = portal.data;
  const guestName = p.guest?.name ?? null;

  return (
    <div className="flex h-dvh flex-col bg-neutral-100">
      <BrandBar />
      <ShareHeader
        portal={p}
        versionId={versionId}
        onSelectVersion={(id) => {
          setVersionId(id);
          setPageIndex(0);
          setPending(null);
          setSelectedThreadId(null);
        }}
        downloadUrl={versionId ? api.originalUrl(versionId) : null}
        guestName={guestName}
        onEditName={() => setAskName(true)}
        tool={tool}
        onSelectTool={setTool}
      />

      <div className="min-h-0 flex-1">
        {p.versions.length === 0 ? (
          <div className="flex h-full items-center justify-center p-8 text-center text-sm text-neutral-500">
            There's nothing to review here yet.
          </div>
        ) : version.isPending || !version.data ? (
          <div className="flex h-full items-center justify-center">
            <Spinner />
          </div>
        ) : (
          <div className="flex h-full min-h-0">
            <div className="min-h-0 flex-1">
              <ProofViewer
                key={version.data.id}
                version={version.data}
                pageIndex={pageIndex}
                onPageChange={(i) => {
                  setPageIndex(i);
                  setPending(null);
                  setSelectedThreadId(null);
                }}
                threads={threads.data ?? []}
                tool={p.canComment ? tool : 'hand'}
                pending={pending}
                onPlaceAnchor={(anchorType, anchor) => {
                  setPending({ anchorType, anchor });
                  // The composer lives inside the rail, so a mark placed while
                  // it's collapsed would go nowhere visible.
                  setCommentsOpen(true);
                }}
                selectedThreadId={selectedThreadId}
                onSelectThread={(id) => {
                  setSelectedThreadId(id);
                  setCommentsOpen(true);
                }}
                onSelectTool={setTool}
              />
            </div>
            {commentsOpen ? (
              <>
                {/* Tap the proof to put the sheet away (phones only — on desktop
                    the rail is a permanent sidebar). */}
                <SheetBackdrop onDismiss={() => setCommentsOpen(false)} />
                <GuestComments
                  token={token}
                  version={version.data}
                  pageIndex={pageIndex}
                  threads={threads.data ?? []}
                  loading={threads.isPending}
                  canComment={p.canComment}
                  guestId={p.guest?.id ?? null}
                  pending={pending}
                  onCancelPending={() => {
                    setPending(null);
                    // Dropping the mark on a phone hands the proof back rather
                    // than leaving the sheet sitting over it.
                    if (isPhone()) setCommentsOpen(false);
                  }}
                  onPosted={() => setPending(null)}
                  selectedThreadId={selectedThreadId}
                  onSelectThread={(id, page) => {
                    if (page !== pageIndex) {
                      setPageIndex(page);
                      setPending(null);
                    }
                    setSelectedThreadId(id);
                  }}
                  onCollapse={() => setCommentsOpen(false)}
                />
              </>
            ) : (
              <CollapsedCorrectionsRail
                count={(threads.data ?? []).length}
                onExpand={() => setCommentsOpen(true)}
              />
            )}
          </div>
        )}
      </div>

      {askName && (
        <NamePrompt
          initial={guestName ?? ''}
          onSubmit={async (name) => {
            await api.identify(name);
            sessionStorage.setItem(`ksq-share-greeted-${token}`, '1');
            setAskName(false);
            await queryClient.invalidateQueries({ queryKey: ['share', token] });
          }}
          onSkip={() => {
            sessionStorage.setItem(`ksq-share-greeted-${token}`, '1');
            setAskName(false);
          }}
        />
      )}
    </div>
  );
}

/**
 * Slim brand strip above the review chrome. A guest arrived here from an email
 * with no idea what this tool is; this names it and offers a way to find out,
 * without competing with the proof for attention.
 */
function BrandBar() {
  return (
    <div className="flex shrink-0 items-center gap-3 border-b border-neutral-200 bg-white px-4 py-1.5">
      <a
        href="/"
        target="_blank"
        rel="noopener noreferrer"
        title="KatSquare Draft — proofing and approvals for print teams"
        className="text-sm font-bold tracking-tight text-neutral-900 hover:text-neutral-600"
      >
        KatSquare Draft
      </a>
      <span className="hidden text-xs text-neutral-400 sm:inline">
        Proofing &amp; approvals for print teams
      </span>
      <a
        href="/"
        target="_blank"
        rel="noopener noreferrer"
        className="ml-auto shrink-0 text-xs font-medium text-neutral-600 underline-offset-2 hover:text-neutral-900 hover:underline"
      >
        What is this?
      </a>
    </div>
  );
}

/** Tools a guest can mark with. Mirrors the members' viewer, minus compare. */
const TOOLS: { id: DrawTool; label: string; Icon: typeof IconPin }[] = [
  { id: 'text', label: 'Select text', Icon: IconText },
  { id: 'pin', label: 'Pin', Icon: IconPin },
  { id: 'box', label: 'Box', Icon: IconBox },
  { id: 'freehand', label: 'Draw', Icon: IconDraw },
  { id: 'hand', label: 'Pan', Icon: IconHand },
];

function ShareHeader({
  portal,
  versionId,
  onSelectVersion,
  downloadUrl,
  guestName,
  onEditName,
  tool,
  onSelectTool,
}: {
  portal: SharePortal;
  versionId: string | null;
  onSelectVersion: (id: string) => void;
  downloadUrl: string | null;
  guestName: string | null;
  onEditName: () => void;
  tool: DrawTool | null;
  onSelectTool: (t: DrawTool) => void;
}) {
  return (
    <header className="shrink-0 border-b border-neutral-200 bg-white">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-2.5">
        <div className="min-w-0">
          <h1 className="truncate text-sm font-semibold text-neutral-900">{portal.proofName}</h1>
          <p className="truncate text-xs text-neutral-500">
            {portal.projectName} · {portal.issueName}
          </p>
        </div>

        {portal.versions.length > 1 && (
          <select
            value={versionId ?? ''}
            onChange={(e) => onSelectVersion(e.target.value)}
            title="Round"
            className="rounded border border-neutral-200 bg-white px-2 py-1 text-xs text-neutral-700 outline-none focus:border-neutral-400"
          >
            {portal.versions.map((v) => (
              <option key={v.id} value={v.id}>
                Round v{v.number}
              </option>
            ))}
          </select>
        )}

        {portal.canComment && (
          <div className="flex items-center gap-0.5 rounded-md border border-neutral-200 p-0.5">
            {TOOLS.map(({ id, label, Icon }) => (
              <button
                key={id}
                type="button"
                title={label}
                aria-label={label}
                onClick={() => onSelectTool(id)}
                className={`flex h-7 w-7 items-center justify-center rounded ${
                  tool === id ? 'bg-neutral-900 text-white' : 'text-neutral-500 hover:bg-neutral-100'
                }`}
              >
                <Icon className="h-4 w-4" />
              </button>
            ))}
          </div>
        )}

        <div className="ml-auto flex items-center gap-2">
          {portal.allowDownload && downloadUrl && (
            <a
              href={downloadUrl}
              className="inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium text-neutral-600 hover:bg-neutral-100"
            >
              <IconDownload className="h-4 w-4" />
              Download
            </a>
          )}
          {portal.canComment && (
            <button
              type="button"
              onClick={onEditName}
              title="Change the name shown on your comments"
              className="rounded-md border border-neutral-200 px-2 py-1 text-xs text-neutral-600 hover:bg-neutral-100"
            >
              {guestName ? `Commenting as ${guestName}` : 'Add your name'}
            </button>
          )}
        </div>
      </div>

      <p className="border-t border-neutral-100 bg-amber-50 px-4 py-1.5 text-[11px] text-amber-800">
        {portal.canComment
          ? 'You’re reviewing through a shared link. Anyone with this link can read this PDF and its comments — please don’t share anything confidential here.'
          : 'You’re viewing through a shared link. Commenting is turned off for this link.'}
      </p>
    </header>
  );
}

/** Name prompt shown on arrival. Skippable by design — the link is anonymous. */
function NamePrompt({
  initial,
  onSubmit,
  onSkip,
}: {
  initial: string;
  onSubmit: (name: string) => Promise<void>;
  onSkip: () => void;
}) {
  const [name, setName] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!name.trim()) return onSkip();
    setBusy(true);
    setError(null);
    try {
      await onSubmit(name.trim());
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <Card className="w-full max-w-sm p-6">
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-neutral-400">
          KatSquare Draft
        </p>
        <h2 className="mb-1 text-base font-semibold text-neutral-900">Introduce yourself</h2>
        <p className="mb-4 text-sm text-neutral-500">
          Adding your name helps the team know who asked for what. It's entirely optional — skip it
          and your comments will simply show as “Guest”.
        </p>
        <Input
          value={name}
          autoFocus
          maxLength={60}
          placeholder="e.g. Priya from Meridian Press"
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void submit();
          }}
        />
        {error && <p className="mt-2 text-xs text-red-500">{error}</p>}
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={onSkip} disabled={busy}>
            Skip
          </Button>
          <Button onClick={() => void submit()} disabled={busy}>
            {busy ? 'Saving…' : 'Continue'}
          </Button>
        </div>
      </Card>
    </div>
  );
}

/**
 * Strip shown in place of the rail when it's put away. The whole strip is the
 * target, not just the chevron, so it's reachable with a thumb.
 */
function CollapsedCorrectionsRail({ count, onExpand }: { count: number; onExpand: () => void }) {
  return (
    <aside
      onClick={onExpand}
      title="Show corrections"
      className="flex w-10 shrink-0 cursor-pointer flex-col items-center border-l border-neutral-200 bg-white py-2 hover:bg-neutral-50"
    >
      <button
        type="button"
        onClick={onExpand}
        aria-label="Show corrections"
        className="flex h-8 w-8 items-center justify-center rounded text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800"
      >
        <IconChevronDown className="h-4 w-4 rotate-90" />
      </button>
      <div className="mt-2 flex flex-col items-center gap-2">
        <IconComment className="h-4 w-4 text-neutral-400" />
        {count > 0 && (
          <span className="flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-blue-600 px-1 text-[10px] font-semibold text-white">
            {count}
          </span>
        )}
      </div>
      <span className="mt-2 text-[11px] font-medium text-neutral-400 [writing-mode:vertical-rl]">
        Corrections
      </span>
    </aside>
  );
}

/**
 * The guest comment rail. A trimmed cousin of the members' panel: no mentions
 * (there's no directory to mention from), no resolve/reopen, and delete only on
 * one's own threads.
 */
function GuestComments({
  token,
  version,
  pageIndex,
  threads,
  loading,
  canComment,
  guestId,
  pending,
  onCancelPending,
  onPosted,
  selectedThreadId,
  onSelectThread,
  onCollapse,
}: {
  token: string;
  version: VersionDetail;
  pageIndex: number;
  threads: CommentThread[];
  loading: boolean;
  canComment: boolean;
  guestId: string | null;
  pending: PendingAnchor | null;
  onCancelPending: () => void;
  onPosted: () => void;
  selectedThreadId: string | null;
  onSelectThread: (id: string, pageIndex: number) => void;
  onCollapse: () => void;
}) {
  const api = useMemo(() => shareApi(token), [token]);
  const queryClient = useQueryClient();
  // The sheet mounts under the finger that opened it, so its close controls
  // ignore anything arriving in that same gesture.
  const settled = useSettledAfterMount();
  const collapse = () => {
    if (settled()) onCollapse();
  };
  const [body, setBody] = useState('');
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['share-threads', token, version.id] });

  const pageId = version.pages[pageIndex]?.id;

  const create = useMutation({
    mutationFn: (text: string) =>
      api.createThread(pageId as string, {
        anchorType: (pending as PendingAnchor).anchorType,
        anchor: (pending as PendingAnchor).anchor,
        body: text,
      }),
    onSuccess: (thread) => {
      setBody('');
      onPosted();
      onSelectThread(thread.id, thread.pageIndex);
      void invalidate();
      // The identity cookie is minted on the first write, so refresh the header.
      void queryClient.invalidateQueries({ queryKey: ['share', token] });
    },
  });

  const ordered = [...threads].sort(
    (a, b) => a.pageIndex - b.pageIndex || a.anchor.y - b.anchor.y || a.anchor.x - b.anchor.x,
  );
  const numberOf = (t: CommentThread) =>
    threads.filter((x) => x.pageIndex === t.pageIndex).indexOf(t) + 1;

  return (
    <aside
      // Phones: a bottom sheet over the lower half of the screen, so the proof
      // stays visible (and tappable, to dismiss). Height comes from .ksq-sheet.
      // Desktop: the fixed sidebar.
      className="ksq-sheet fixed inset-x-0 bottom-0 z-[36] flex flex-col rounded-t-2xl border-t border-neutral-200 bg-white shadow-[0_-8px_30px_rgba(0,0,0,0.18)] md:static md:z-auto md:h-auto md:w-80 md:shrink-0 md:rounded-none md:border-l md:border-t-0 md:shadow-none"
    >
      <button
        type="button"
        onClick={collapse}
        aria-label="Hide corrections"
        className="flex justify-center pb-0.5 pt-2 md:hidden"
      >
        <span className="h-1 w-9 rounded-full bg-neutral-300" />
      </button>
      <div className="flex items-center gap-2 border-b border-neutral-200 px-4 py-2.5">
        <h2 className="text-sm font-semibold text-neutral-900">
          Corrections <span className="font-normal text-neutral-400">· {threads.length}</span>
        </h2>
        <button
          type="button"
          onClick={collapse}
          title="Hide corrections"
          className="ml-auto flex h-6 w-6 shrink-0 items-center justify-center rounded text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"
        >
          <IconChevronDown className="h-4 w-4 -rotate-90" />
        </button>
      </div>

      {pending && canComment && (
        <div className="border-b border-neutral-200 p-3">
          <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-neutral-400">
            New {pending.anchorType.toLowerCase()} correction
          </p>
          <textarea
            value={body}
            autoFocus
            rows={3}
            placeholder="Describe the change you'd like…"
            onChange={(e) => setBody(e.target.value)}
            className="w-full resize-none rounded-md border border-neutral-300 px-2 py-1.5 text-sm outline-none focus:border-neutral-900"
          />
          <div className="mt-2 flex justify-end gap-2">
            <Button
              variant="ghost"
              className="px-2 py-1 text-xs"
              onClick={() => {
                if (!settled()) return;
                setBody('');
                onCancelPending();
              }}
            >
              Cancel
            </Button>
            <Button
              className="px-2 py-1 text-xs"
              disabled={!body.trim() || create.isPending}
              onClick={() => create.mutate(body.trim())}
            >
              {create.isPending ? 'Posting…' : 'Add correction'}
            </Button>
          </div>
          {create.isError && (
            <p className="mt-1 text-xs text-red-500">{(create.error as Error).message}</p>
          )}
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : ordered.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-neutral-400">
            {canComment
              ? 'No corrections yet. Pick a tool above, mark the page, and describe the change.'
              : 'No corrections yet.'}
          </p>
        ) : (
          <ul>
            {ordered.map((t) => (
              <GuestThreadCard
                key={t.id}
                token={token}
                versionId={version.id}
                thread={t}
                index={numberOf(t)}
                pageLabel={version.pages.length > 1 ? `Page ${t.pageIndex + 1}` : null}
                canComment={canComment}
                mine={guestId != null && t.createdByKind === 'GUEST' && t.createdBy?.id === guestId}
                selected={t.id === selectedThreadId}
                onSelect={() => onSelectThread(t.id, t.pageIndex)}
                onChanged={invalidate}
              />
            ))}
          </ul>
        )}
      </div>

      {/* Sits under the list rather than over the proof: a reviewer who has
          finished marking up is the one most likely to wonder what this is. */}
      <div className="shrink-0 border-t border-neutral-200 px-4 py-2.5">
        <p className="text-[11px] leading-snug text-neutral-400">
          Corrections collected with{' '}
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-neutral-600 underline-offset-2 hover:text-neutral-900 hover:underline"
          >
            KatSquare Draft
          </a>{' '}
          — proofing, versions and sign-off for print teams.
        </p>
      </div>
    </aside>
  );
}

function GuestThreadCard({
  token,
  versionId,
  thread,
  index,
  pageLabel,
  canComment,
  mine,
  selected,
  onSelect,
  onChanged,
}: {
  token: string;
  versionId: string;
  thread: CommentThread;
  index: number;
  pageLabel: string | null;
  canComment: boolean;
  /** True when this guest opened the thread — only then may they delete it. */
  mine: boolean;
  selected: boolean;
  onSelect: () => void;
  onChanged: () => void;
}) {
  const api = useMemo(() => shareApi(token), [token]);
  const queryClient = useQueryClient();
  const [reply, setReply] = useState('');
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const resolved = thread.status === 'RESOLVED';

  const addReply = useMutation({
    mutationFn: (text: string) => api.addComment(thread.id, { body: text }),
    onSuccess: () => {
      setReply('');
      onChanged();
      void queryClient.invalidateQueries({ queryKey: ['share', token] });
    },
  });
  const remove = useMutation({
    mutationFn: () => api.deleteThread(thread.id),
    onSuccess: onChanged,
  });

  const ref = useRef<HTMLLIElement>(null);
  useEffect(() => {
    if (selected) ref.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [selected]);

  return (
    <li
      ref={ref}
      onClick={onSelect}
      data-version={versionId}
      className={`cursor-pointer border-b border-neutral-100 px-4 py-3 ${
        selected ? 'bg-amber-50 ring-1 ring-inset ring-amber-300' : 'hover:bg-neutral-50'
      }`}
    >
      <div className="mb-1 flex items-center gap-1.5">
        <span
          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold text-white ${
            resolved ? 'bg-emerald-500' : 'bg-blue-600'
          }`}
        >
          {index}
        </span>
        {pageLabel && (
          <span className="shrink-0 rounded bg-neutral-100 px-1.5 py-0.5 text-[10px] font-medium text-neutral-500">
            {pageLabel}
          </span>
        )}
        {resolved && <span className="text-[11px] font-medium text-emerald-600">Done</span>}
        {mine && canComment && (
          <div className="ml-auto flex items-center gap-0.5">
            {confirmingDelete ? (
              <>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setConfirmingDelete(false);
                    remove.mutate();
                  }}
                  className="rounded px-1.5 py-0.5 text-[11px] font-semibold text-red-600 hover:bg-red-50"
                >
                  Delete
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setConfirmingDelete(false);
                  }}
                  className="rounded px-1 text-[11px] text-neutral-400 hover:text-neutral-700"
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirmingDelete(true);
                }}
                title="Delete this correction"
                className="rounded p-1 text-neutral-400 hover:bg-neutral-100 hover:text-red-600"
              >
                ✕
              </button>
            )}
          </div>
        )}
      </div>

      {thread.anchorType === 'TEXT' && thread.anchor.quote && (
        <p className="mb-1.5 line-clamp-2 border-l-2 border-amber-400 bg-amber-50 py-0.5 pl-2 text-xs italic text-neutral-600">
          “{thread.anchor.quote}”
        </p>
      )}

      <div className="space-y-1.5">
        {thread.comments.map((c) => (
          <div key={c.id}>
            <div className="flex items-baseline gap-1.5">
              <span className="text-xs font-semibold text-neutral-800">
                {c.author?.name ?? 'Unknown'}
              </span>
              {c.authorKind === 'USER' && (
                <span className="rounded bg-neutral-100 px-1 py-px text-[9px] font-medium uppercase tracking-wide text-neutral-500">
                  Team
                </span>
              )}
              <span className="text-[10px] text-neutral-400">{timeAgo(c.createdAt)}</span>
            </div>
            <p className="whitespace-pre-wrap text-sm leading-snug text-neutral-700">{c.body}</p>
          </div>
        ))}
      </div>

      {canComment && (
        <div className="mt-1.5 flex gap-1.5" onClick={(e) => e.stopPropagation()}>
          <input
            value={reply}
            placeholder="Reply…"
            onChange={(e) => setReply(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && reply.trim()) addReply.mutate(reply.trim());
            }}
            className="min-w-0 flex-1 rounded border border-neutral-200 px-2 py-1 text-xs outline-none focus:border-neutral-900"
          />
          <button
            type="button"
            disabled={!reply.trim() || addReply.isPending}
            onClick={() => addReply.mutate(reply.trim())}
            className="rounded bg-neutral-900 px-2 py-1 text-xs font-medium text-white disabled:opacity-40"
          >
            Send
          </button>
        </div>
      )}
    </li>
  );
}

/** Compact relative timestamp ("just now", "5m", "3h", "2d", else a date). */
function timeAgo(iso: string): string {
  const secs = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 45) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d`;
  return new Date(iso).toLocaleDateString();
}
